import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

type Translations = Record<string, Record<string, string>>;
export type Lang = 'en' | 'cs';

interface I18nContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  ready: boolean;
}

const I18nContext = createContext<I18nContextType>({
  lang: 'en',
  setLang: () => {},
  t: (key) => key,
  ready: false,
});

export const SUPPORTED: Lang[] = ['en', 'cs'];
const STORAGE_KEY = 'lang';

function detectLang(): Lang {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED.includes(stored as Lang)) return stored as Lang;
  const browser = navigator.language.split('-')[0];
  return SUPPORTED.includes(browser as Lang) ? (browser as Lang) : 'en';
}

function parseTsv(text: string): Translations {
  const lines = text.split('\n').filter(l => l.trim());
  const header = lines[0].split('\t');
  const translations: Translations = {};
  for (const lang of header.slice(1)) translations[lang] = {};
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split('\t');
    const key = cols[0];
    for (let j = 1; j < header.length; j++) {
      translations[header[j]][key] = cols[j] || key;
    }
  }
  return translations;
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(detectLang);
  const [translations, setTranslations] = useState<Translations | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    fetch('/translations.tsv')
      .then(r => r.text())
      .then(text => { setTranslations(parseTsv(text)); setReady(true); })
      .catch(() => setReady(true));
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    localStorage.setItem(STORAGE_KEY, l);
  }, []);

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    if (translations == null) {
      return '';
    }
    let val = translations[lang]?.[key] || translations['en']?.[key];
    if (!val) {
      console.error(`key "${key}" not found  for language ${lang}`);
      val = key;
    }
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        val = val.replace(`{${k}}`, String(v));
      }
    }
    return val;
  }, [lang, translations]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, ready }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslation = () => useContext(I18nContext);
